import { reactive, ref, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useVueFlow } from "@vue-flow/core"
import useVuelidate from '@vuelidate/core'
import { nodeType } from '@/core/helper/enums/nodeType'
import { required } from '@vuelidate/validators'
import { toast, variant } from '@/core/helper/utility'
import { httpStatusCode } from '@/core/helper/httpStatusCode';
import { GetAll, Create, GetByGuid, Delete } from "@/infrastructure/repositories/botRepository"

export const useBot = () => {
    const router = useRouter();
    const { 
        findNode, 
        nodes, 
        edges,
        addNodes, 
        addEdges, 
        project, 
        vueFlowRef, 
        onConnect, 
        setNodes, 
        setEdges, 
        setViewport 
    } = useVueFlow({
        nodes: [
          {
            id: '1',
            type: nodeType.StartNode,
            label: nodeType.StartNode,
            position: { x: 100, y: 50 },
            handleBounds: {
                source: [{
                    id: null,
                    key: null,
                    position: "bottom",
                    nodeId: "1",
                    type: "source",
                    x: 88.40625,
                    y: 69,
                    width: 6,
                    height: 6
                }],
                target: []
            },
          },
          {
            id: '2',
            type: nodeType.EndNode,
            label: nodeType.EndNode,
            position: { x: 860, y: 350 },
            handleBounds: {
                source: [{
                    id: null,
                    position: "left",
                    nodeId: "2",
                    type: "source",
                    x: -3,
                    y: 33,
                    width: 6,
                    height: 6
                }],
                target: []
            },
          }
        ]
    })

    var filter = reactive({});
    var bots = ref([]);
    var bot = ref({
        name: null,
        idBotType: null,
        idBrand: null,
    });
    var isLoading = ref(false);
    var isCreateLoading = ref(false);
    var isDeleteLoading = ref(false);

    const botRules = {
        name: { required },
        idBotType: { required },
        idBrand: { required }
    }

    const validate$ = useVuelidate(botRules, bot, { $scope: false });

    const onCreateHandle = async () => {
        try {
            const isValid = await validate$.value.$validate();
            if(!isValid){
                return;
            }

            isCreateLoading.value = true;

            var model = {
                id: bot.value.id,
                name: bot.value.name,
                idBotType: bot.value.idBotType,
                idBrand: bot.value.idBrand,
                nodes: nodes.value.map((node) => {
                    return {
                        id: node.key ?? 0,
                        idNode: node.id,
                        label: node.label,
                        nodeType: node.type,
                        width: node.dimensions.width,
                        height: node.dimensions.height,
                        computedPositionX: node.computedPosition.x,
                        computedPositionY: node.computedPosition.y,
                        computedPositionZ: node.computedPosition.z,
                        name: node.data.name,
                        value: node.data.value,
                        selected: node.selected,
                        dragging: node.dragging,
                        resizing: node.resizing,
                        initialized: node.initialized,
                        isParent: node.isParent,
                        positionX: node.position.x,
                        positionY: node.position.y,
                        sources: node.handleBounds.source.map((source) => {
                            return {
                                id: source.key ?? 0,
                                idSource: source.id,
                                position: source.position,
                                idNode: source.nodeId,
                                type: source.type,
                                x: source.x,
                                y: source.y,
                                width: source.width,
                                height: source.height,
                            }
                        }),
                        targets: node.handleBounds.target.map((target) => {
                            return {
                                id: target.key ?? 0,
                                idTarget: target.id,
                                position: target.position,
                                idNode: target.nodeId,
                                type: target.type,
                                x: target.x,
                                y: target.y,
                                width: target.width,
                                height: target.height,
                            }
                        }),

                    }
                }),
                edges: edges.value.map((edge) => {
                    return {
                        id: edge.key ?? 0,
                        idEdge: edge.id,
                        type: edge.type,
                        source: edge.source,
                        target: edge.target,
                        sourceHandle: null,
                        targetHandle: null,
                        data: {},
                        events: {},
                        label: edge.label,
                        sourceNode: {},
                        targetNode: {},
                        sourceX: edge.sourceX,
                        sourceY: edge.sourceY,
                        targetX: edge.targetX,
                        targetY: edge.targetY,
                    }
                })
            };

            var response = await Create(model);

            if(response.status == httpStatusCode.OK){
                toast({
                    variant: variant.success,
                    title: response.data.title,
                    body:  response.data.message
                });

                router.push({
                    name: 'answerBotCreate',
                    params: {
                        guid: response.data.value.guid
                    }
                })
            }else{
                toast({
                    collection: true,
                    variant: variant.error,
                    title: response.data.title,
                    body:  response.data.messages
                });
            }

            isCreateLoading.value = false;
        } catch (error) {
            isCreateLoading.value = false;
            toast({
                variant: variant.error,
                title: 'Bots',
                body: 'Could not create bot, check again, please contact support center'
            });
    
            throw new Error(error);
        }
    }

    const getBots = async () => {
        try {
            isLoading.value = true;

            var response = await GetAll(filter);
            if(response.status == httpStatusCode.OK){
                bots.value = response.data.value;
            }else{
                toast({
                    collection: true,
                    variant: variant.error,
                    title: response.data.title,
                    body:  response.data.messages
                });
            }
            isLoading.value = false;
        } catch (error) {
            isLoading.value = false;
            toast({
                variant: variant.error,
                title: 'Bots',
                body: 'Could not get bots list, check again, please contact support center'
            });
    
            throw new Error(error);
        }
    }

    const getBotByGuid = async (guid) => {
        try {
            isLoading.value = true;

            var response = await GetByGuid(guid);

            if(response.status == httpStatusCode.OK){
                bot.value = response.data.value;

                let nodes  =  response.data.value.nodes.map((node) => {
                    return {
                        id: node.idNode,
                        key: node.id ?? 0,
                        type: node.nodeType,
                        label: node.label,
                        computedPosition: {
                            x: node.computedPositionX,
                            y: node.computedPositionY,
                            z: node.computedPositionZ,
                        },
                        selected: node.selected,
                        dragging: node.dragging,
                        resizing: node.resizing,
                        initialized: node.initialized,
                        isParent: node.isParent,
                        position: {
                            x: node.positionX,
                            y: node.positionY
                        },
                        data: {
                            name: node.name,
                            value: node.value
                        },
                        events: {},
                    }
                });

                let edges  = response.data.value.edges.map((edge) => {
                    return {
                        id: edge.idEdge,
                        key: edge.id ?? 0,
                        type: edge.type,
                        source: edge.source,
                        target: edge.target,
                        sourceHandle: null,
                        targetHandle: null,
                        data: {},
                        events: {},
                        label: edge.label,
                        sourceNode: {},
                        targetNode: {},
                        sourceX: edge.sourceX,
                        sourceY: edge.sourceY,
                        targetX: edge.targetX,
                        targetY: edge.targetY
                    }
                });

                setNodes(nodes);
                setEdges(edges);
            }else{
                toast({
                    collection: true,
                    variant: variant.error,
                    title: response.data.title,
                    body:  response.data.messages
                });
            }
            isLoading.value = false;
        } catch (error) {
            isLoading.value = false;
            toast({
                variant: variant.error,
                title: 'Bots',
                body: 'Could not get bots list, check again, please contact support center'
            });
    
            throw new Error(error);
        }
    }

    const onDeleteHandle = async (guid, index) => {
        try {
            isDeleteLoading.value = true;

            var response = await Delete(guid);
            if(response.status == httpStatusCode.OK){
                bots.value.splice(index, 1);
                
                toast({
                    variant: variant.success,
                    title: response.data.title,
                    body:  response.data.message
                });
            }else{
                toast({
                    collection: true,
                    variant: variant.error,
                    title: response.data.title,
                    body:  response.data.messages
                });
            }
            isDeleteLoading.value = false;
        } catch (error) {
            isDeleteLoading.value = false;
            toast({
                variant: variant.error,
                title: 'Bots',
                body: 'Could not disable bot, check again, please contact support center'
            });
    
            throw new Error(error);
        }
    }

    onConnect((params) => {
        addEdges(params)
    });

    const handleOnDrop = (event) => {
        const type = event.dataTransfer?.getData('application/vueflow')
        if (type === 'workflow') {
            const { nodes, edges, position, zoom } = []
            const [x = 0, y = 0] = position
            setNodes(nodes)
            setEdges(edges)
            setViewport({ x, y, zoom: zoom || 0 })
            return
        }

        const { left, top } = vueFlowRef.value?.getBoundingClientRect()

        const position = project({
            x: event.clientX - left,
            y: event.clientY - top
        })

        const newNode = {
            id: (nodes.value.length + 1).toString(),
            type,
            position,
            label: `${type} node`,
            data: {
                title: type,
            }
        }

        addNodes([newNode])

        nextTick(() => {
            const node = findNode(newNode.id)
            const stop = watch(
            () => node?.dimensions,
            (dimensions) => {
                if (dimensions.width > 0 && dimensions.height > 0 && node) {
                node.position = {
                    x: node.position.x - node.dimensions.width / 2,
                    y: node.position.y - node.dimensions.height / 2
                }
                stop()
                }
            },
            { deep: true, flush: 'post' }
            )
        })
    }

    const handleOnDragOver = (event) => {
        event.preventDefault()

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move'
        }
    }

    return {
        validate$,
        isLoading,
        isCreateLoading,
        isDeleteLoading,
        bots,
        bot,
        nodes,
        edges,
        getBots,
        getBotByGuid,
        onCreateHandle,
        onDeleteHandle,
        handleOnDrop,
        handleOnDragOver
    }
}